import {remCalc} from '../../_utils'
import {BRAND_KEY} from '../../../brand'

export default {
    baseStyle: (props: {
        backgroundColor?: string
        hasPaddings?: boolean
        isPdpPage?: boolean
        isFull?: boolean
        isReversed?: boolean
        hasDetails?: boolean
        isExpanded?: boolean
    }) => {
        const {
            backgroundColor = 'white',
            hasPaddings,
            isPdpPage,
            isFull,
            isReversed,
            hasDetails,
            isExpanded,
        } = props

        const isKenwood = BRAND_KEY === ('kenwood' as string)
        const isBraun = BRAND_KEY === ('braun' as string)

        const colors = {
            backgroundColor: isPdpPage
                ? 'background.default'
                : backgroundColor === 'grey'
                ? 'background.light'
                : backgroundColor === 'black'
                ? 'background.dark'
                : 'background.default',
            label: {
                color: backgroundColor === 'black' ? 'text.inverse' : 'text.secondary',
            },
            text: {
                color: backgroundColor === 'black' ? 'text.inverse' : 'text.default',
            },
            buttonVariant: isKenwood
                ? 'primary'
                : isBraun && backgroundColor === 'black'
                ? 'inverse-secondary'
                : backgroundColor === 'black'
                ? 'inverse-secondary'
                : 'secondary',
            socialIconsBorders:
                backgroundColor === 'black'
                    ? {
                          borderWidth: `${remCalc(1)}`,
                          borderStyle: 'solid',
                          borderRadius: `${remCalc(10)}`,
                          borderColor: 'rgba(255, 255, 255, 0.3)',
                      }
                    : undefined,
        }

        const styles = {
            ...colors,
            container: {
                backgroundColor: colors.backgroundColor,
                maxWidth: remCalc(1920),
                margin: '0 auto',
            },

            mobileOnlyTitle: {
                display: {base: 'block', lg: 'none'}, // Visible only on mobile
                width: '100%',
                textAlign: 'center',
                marginBottom: remCalc(16),
            },

            mainContainer: {
                width: '100%',
                alignItems: 'center',
                justifyContent: {
                    base: 'center',
                    lg: isFull ? (isReversed ? 'flex-end' : 'flex-start') : 'center',
                },
                py: hasPaddings
                    ? {
                          base: isPdpPage ? remCalc(16) : remCalc(24),
                          md: isPdpPage ? remCalc(24) : remCalc(40),
                          lg: remCalc(40),
                      }
                    : {
                          base: remCalc(24),
                          lg: 'inherit',
                      },
            },
            outterContentContainer: {
                flexDirection: {
                    base: 'column',
                    md: isPdpPage ? (isReversed ? 'row-reverse' : 'row') : 'column',
                    lg: isReversed ? 'row-reverse' : 'row',
                },
                alignItems: {
                    base: hasDetails ? 'flex-start' : 'center',
                    md: isPdpPage ? 'stretch' : 'center',
                    lg: 'center',
                },
                justifyContent: {
                    base: 'center',
                    lg: isFull ? undefined : 'space-between',
                },
                mx: {
                    base: hasDetails ? remCalc(24) : remCalc(16),
                    md: hasDetails ? remCalc(24) : remCalc(107),
                    lg: 0,
                },
                paddingX: {
                    base: 0,
                    lg: isFull || isPdpPage ? undefined : remCalc(24),
                },
                gap: {
                    base: 0,
                    lg: isFull ? remCalc(142) : remCalc(24),
                },
                width: {base: 'auto', lg: '100%'},
                height: 'fit-content',
                maxWidth: {
                    base: '100%',
                    lg: isFull ? undefined : remCalc(1440),
                },
            },
            mediaContainer: {
                width: '100%',
                marginBottom: {
                    base: isPdpPage && !hasDetails ? 0 : remCalc(16),
                    md: isPdpPage && !hasDetails ? 0 : remCalc(24),
                    lg: 0,
                },
                paddingBottom: {
                    base: isPdpPage && !hasDetails ? remCalc(16) : 0,
                    md: isPdpPage && !hasDetails ? remCalc(24) : 0,
                    lg: 0,
                },
                maxWidth: {
                    base: '100%',
                    lg: isFull ? remCalc(1067) : remCalc(807),
                },
                position: 'relative',
            },
            textContainer: {
                width: {
                    base: '100%',
                    lg: 'auto',
                },
                minWidth: {
                    base: 'auto',
                    md: isPdpPage ? remCalc(476) : remCalc(451),
                    lg: isPdpPage ? '30%' : remCalc(451),
                },
                maxWidth: {
                    base: '100%',
                    md: isPdpPage ? remCalc(476) : '100%',
                    lg: isPdpPage ? `calc(100% - ${remCalc(973)})` : remCalc(332),
                },
                display: 'flex',
                flexDirection: 'column',
                alignItems: {base: 'center', lg: 'flex-start'},
                alignment: 'center',
            },

            titleWrapper: {
                display: {base: 'none', lg: 'block'},
            },
            socialIconsContainer: {
                gap: remCalc(16),
                direction: {base: 'column', md: 'row'},
                alignItems: 'center',
                justifyContent: {base: 'center', lg: 'flex-start'},
            },
            verticalDetailedFullSizePdp: {
                position: 'relative',
                alignItems: 'center',
                marginTop: remCalc(16),
            },
            verticalDetailedPdp: {
                position: 'relative',
                transition: 'all .2s ease-in-out',
                overflow: 'hidden',
                alignItems: 'center',
                _after: {
                    content: "''",
                    position: 'absolute',
                    top: !isExpanded ? '0' : 'unset',
                    left: !isExpanded ? '0' : 'unset',
                    right: !isExpanded ? '0' : 'unset',
                    bottom: !isExpanded ? '0' : 'unset',
                    bg: 'linear-gradient(180deg, rgba(255,255,255,0), rgba(255,255,255,1))',
                    opacity: !isExpanded ? '1' : '0',
                    transition: 'all .2s ease-in-out',
                },
            },
            toggleButton: {
                mt: remCalc(16),
                px: '0',
                py: '0',
                height: 'auto',
                ...(isKenwood && {
                    textStyle: 'body-xsmall',
                    fontWeight: 'bold',
                }),
            },
            video: {
                width: '100%',
                height: '100%',
            },
            textTitleDescription: {
                childrenTextStyle: 'body-large',
                alignment: 'left',
                textStyle: 'head-large',
                titleSpacing: remCalc(24),
                fontWeight: 'medium',
            },
            textTitleLabel: {
                alignment: {base: 'center', lg: 'left'},
                textStyle: 'body-small',
                mb: remCalc(24),
                color: colors.label.color,
                fontWeight: 'bold',
            },
            sanitizedHtmlPdp: {
                ul: {
                    ml: remCalc(25),
                },
                li: {
                    pb: remCalc(5),
                },
                a: {
                    color: 'blue.500',
                    textDecoration: 'underline',
                    _hover: {
                        color: 'blue.600',
                    },
                },
            },
            textTitleDescriptionStandard: {
                textStyle: 'head-large',
                childrenTextStyle: 'body-large',
                alignment: {base: 'center', lg: 'left'},
                fontWeight: 'medium',
                titleSpacing: remCalc(24),
                color: colors.text.color,
                childrenTextColor: colors.text.color,
                mb: remCalc(24),
            },
            socialContent: {
                alignItems: {base: 'center', lg: 'flex-start'},
                justifyContent: {base: 'center', lg: 'flex-start'},
            },
        }
        return styles
    },
}
